import type { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { Styles } from 'src/theme/themes';

const baseGrid: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridTemplateRows: '1fr',
  columnGap: 0,
};

const tableRowBase: SxProps<Theme> = (theme: Theme) => ({
  ...baseGrid,
  alignItems: 'center',
  flexGrow: 1,
  paddingLeft: theme.spacing(1.5),
  borderLeftWidth: 4,
  borderLeftStyle: 'solid',
  color: theme.colors.secondary[800],
  borderLeftColor: theme.colors.secondary[500],
  background: alpha(theme.colors.secondary[500], 0.07),
});

const withComparisonColor = (theme: Theme) => ({
  borderLeftColor: theme.colors.comparison[500],
  background: alpha(theme.colors.comparison[500], 0.04),
});

const withSecondaryTone = (theme: Theme) => ({
  borderLeftColor: theme.colors.secondary[100],
  background: alpha(theme.colors.secondary[500], 0.04),
});

const compactRow = (theme: Theme) => ({
  height: theme.spacing(5.25),
  flexGrow: 0,
});

// === Styles ===

export const styles = {
  tableScrollContainer: (theme: Theme) => ({
    overflowX: 'auto',
    overflowY: 'auto',
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    margin: theme.spacing(3),
    paddingBottom: 0,
  }),

  tableContainer: (theme: Theme) => ({
    width: '100%',
    minWidth: theme.spacing(80),
  }),

  tableHeader: (theme: Theme) => ({
    ...baseGrid,
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    paddingRight: 0,
    background: theme.colors.neutral[50],
    border: `1px solid ${theme.colors.neutral[200]}`,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    marginBottom: theme.spacing(0.5),
  }),

  tableGrid: (theme: Theme) => ({
    rowGap: theme.spacing(2),
  }),

  primaryTableRow: (theme: Theme) => ({
    ...tableRowBase(theme),
  }),

  comparisonRow: (theme: Theme) => ({
    ...tableRowBase(theme),
    ...withComparisonColor(theme),
    ...compactRow(theme),
  }),

  secondaryTableRow: (theme: Theme) => ({
    ...tableRowBase(theme),
    ...withSecondaryTone(theme),
    marginTop: theme.spacing(2),
  }),
} satisfies Styles;
